import { useQuery } from '@apollo/client/react';
import { Box, List, ListItem, Typography, useTheme } from '@mui/material';

import { gql } from '@graphql/__generated__';
import type { RentedRoomDetailsFragment } from '@graphql/__generated__/graphql';
import { useLocale } from '@shared/hooks/locale';

export const RENTED_ROOM_DETAILS_FRAGMENT = gql(/* GraphQL */ `
  fragment RentedRoomDetails on lots {
    tenant_number
    minimal_stay
    tenant_gender
    youngest_tenant_age
    oldest_tenant_age
    smoking_allowed
    couples_allowed
    pets_allowed
    occupied_now
    furnished
    owner_living
    property {
      bed_type
      window_view
    }
  }
`);

const GET_RENTED_ROOM_ENUM_LABELS = gql(/* GraphQL */ `
  query GetRentedRoomEnumLabels {
    bed_type_options: get_translated_enum(
      args: { enum_table_name: "bed_type_enum" }
    ) {
      value
      label
    }
    window_view_options: get_translated_enum(
      args: { enum_table_name: "window_view_enum" }
    ) {
      value
      label
    }
    tenant_gender_options: get_translated_enum(
      args: { enum_table_name: "tenant_gender_enum" }
    ) {
      value
      label
    }
  }
`);

const labelFor = (
  options: ReadonlyArray<{ value: string; label?: string | null }> | undefined,
  value?: string | null,
) =>
  value == null ? null : options?.find(o => o.value === value)?.label ?? value;

export const RentedRoomDetails: React.FC<{
  listing: RentedRoomDetailsFragment;
}> = ({ listing }) => {
  const { t } = useLocale();
  const theme = useTheme();
  const { property } = listing;

  const { data } = useQuery(GET_RENTED_ROOM_ENUM_LABELS);

  const yesNo = (value?: boolean | null) =>
    value == null ? null : value ? t('Yes') : t('No');

  const rows: Array<[string, string | number | null | undefined]> = [
    [t('Furnished'), yesNo(listing.furnished)],
    [t('Minimal stay (months)'), listing.minimal_stay],
    [t('Max number of flat mates'), listing.tenant_number],
    [t('Bed type'), labelFor(data?.bed_type_options, property.bed_type)],
    [
      t('Window view'),
      labelFor(data?.window_view_options, property.window_view),
    ],
    [t('Smoking allowed'), yesNo(listing.smoking_allowed)],
    [t('Couples allowed'), yesNo(listing.couples_allowed)],
    [t('Pets allowed'), yesNo(listing.pets_allowed)],
    [t('Currently occupied'), yesNo(listing.occupied_now)],
  ];

  if (listing.occupied_now === true) {
    rows.push(
      [t('Owner lives in the property'), yesNo(listing.owner_living)],
      [
        t('Flatmates gender'),
        labelFor(data?.tenant_gender_options, listing.tenant_gender),
      ],
      [t('Youngest flatmate age'), listing.youngest_tenant_age],
      [t('Oldest flatmate age'), listing.oldest_tenant_age],
    );
  }

  const hasValues = rows.some(([_, value]) => value != null);

  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, mt: 1 }}>
        {t('Rented room')}
      </Typography>
      {!hasValues ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('None')}
        </Typography>
      ) : (
        <List
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              md: 'repeat(2, 1fr)',
            },
            columnGap: 5,
            pt: 0,
            pb: 0.5,
          }}
        >
          {rows.map(([label, value]) =>
            value == null ? null : (
              <ListItem
                key={label}
                divider
                sx={{ justifyContent: 'space-between', gap: 2, minWidth: 0 }}
                disableGutters
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, flexShrink: 0 }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ ...theme.text.ellipsis, textAlign: 'right' }}
                >
                  {value}
                </Typography>
              </ListItem>
            ),
          )}
        </List>
      )}
    </Box>
  );
};
